import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type TableRow = Record<string, unknown> & {
  __originalValues?: Record<string, string>;
  __maskedValues?: Record<string, string>;
  __metaData?: unknown;
};

interface TableSelectionContextValue {
  clearSelection: () => void;
  selectedRows?: TableRow[];
  setSelectedRows: (rows?: TableRow[]) => void;
}

interface TableRowsContextValue {
  allRows: TableRow[];
  setAllRows: (rows: TableRow[]) => void;
}

const TableSelectionContext = createContext<
  TableSelectionContextValue | undefined
>(undefined);

// Split out so data-only consumers don't re-render per selection toggle.
const TableRowsContext = createContext<TableRowsContextValue | undefined>(
  undefined
);

export const useTableSelection = (): TableSelectionContextValue => {
  const ctx = useContext(TableSelectionContext);
  if (!ctx) {
    throw new Error(
      'useTableSelection must be used within TableSelectionProvider'
    );
  }
  return ctx;
};

export const useTableRows = (): TableRowsContextValue => {
  const ctx = useContext(TableRowsContext);
  if (!ctx) {
    throw new Error('useTableRows must be used within TableSelectionProvider');
  }
  return ctx;
};

export const TableSelectionProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [selectedRows, setSelectedRows] = useState<TableRow[]>();
  const [allRows, setAllRows] = useState<TableRow[]>([]);

  const clearSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  // Memoized so each value only changes when its own slice changes.
  const selectionValue = useMemo(
    () => ({ selectedRows, setSelectedRows, clearSelection }),
    [selectedRows, clearSelection]
  );

  const rowsValue = useMemo(() => ({ allRows, setAllRows }), [allRows]);

  return (
    <TableRowsContext.Provider value={rowsValue}>
      <TableSelectionContext.Provider value={selectionValue}>
        {children}
      </TableSelectionContext.Provider>
    </TableRowsContext.Provider>
  );
};
